import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Search, Loader2, AlertCircle } from "lucide-react";
import {
  searchAddresses,
  getAddressDetails,
  type AddressSearchResult,
  type AddressDetails,
} from "@/services/addressService";
import { Button } from "@/components/ui/button";

import { getAddressInformation } from "@/dispatchers/patientDispatchers";

  interface AddressSummary {
  addressLineOne: string;
  addressLineTwo: string;
  postTown: string;
  postalCounty: string;
  postCode: string;
}

interface AddressSearchFieldProps {
  label?: string;
  value?: string;
  onChange: (address: string, details?: AddressDetails) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  isDarkMode?: boolean;
  postcodeValue? : string;
  setPostcodeValue: (postcode: string) => void;
  addresses? : AddressSummary[];
  setAddresses: (addresses: AddressSummary[]) => void;
}


export function AddressSearchField({
  label = "Address",
  value = "",
  onChange,
  placeholder = "Start typing an address...",
  className = "",
  required = false,
  isDarkMode = false,
  postcodeValue,
  setPostcodeValue,
  addresses,
  setAddresses
}: AddressSearchFieldProps) {
  const [displayValue, setDisplayValue] = useState(value); // 👈 separate from searchQuery
  const [searchQuery, setSearchQuery] = useState(""); // 👈 only used for API searches
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingViaPostCode, setIsSearchingViaPostCode] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<string>("");
  const [errorPostCode, setErrorPostCode] = useState<string | null>(null);
  

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ODS_CODE = "FL592";


  // Sync display with external prop
  useEffect(() => {
    if (value !== displayValue) {
      setDisplayValue(value);
    }
  }, [value]);


  const handleManualInput = (val: string) => {
    // setDisplayValue(val);
    // setSearchQuery(val); // trigger API only when typing
  
    // onChange(val);
      const cleaned = val
    .toUpperCase()                // uppercase
    .replace(/[^A-Z0-9 ]/g, "").trim();  // allow only A-Z, 0-9 and space
    
    setPostcodeValue(cleaned);
  // setDisplayValue(cleaned);
  // setSearchQuery(cleaned);
  onChange(cleaned);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

   const validateUkPostCode = (postcode: string) => {
  const ukPostcodeRegex = /^(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/i;
  return ukPostcodeRegex.test(postcode.trim());
};

  // Sync with external value changes

   const fetchAddressesViaPostCode = async () => {
     //
     try{
      if(!validateUkPostCode(postcodeValue)){
        setErrorPostCode("Invalid PostCode");
        return;
      }
      setErrorPostCode(null);
       setIsSearchingViaPostCode(true);
       setAddresses([]);
       setSelectedIndex("");
       const postCode = {
         postcode: postcodeValue,
        };
        const result = await getAddressInformation(postCode);
        setAddresses(result || []);
    }
    catch(ex){
      console.log(ex);
    }
    finally{
      setIsSearchingViaPostCode(false);
    }
  };

const handleSelectAddress = (index: string) => {
  setSelectedIndex(index);
  if (index === "") return;
  const addr = addresses[Number(index)];
    const fullAddress = [
      addr.addressLineOne,
      addr.addressLineTwo,
      addr.postalCounty,
      addr.postTown,
      addr.postCode,
    ]
      .filter(Boolean)
      .join(", ");
  if (!addr) return;
  onChange(fullAddress, addr as any);
};


  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{ zIndex: 1000 }}
    >
      {label && (
        <Label
          htmlFor="address-search"
          className={`transition-colors duration-300 ${
            isDarkMode ? "text-gray-200" : "text-gray-900"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div className="mt-1 flex items-end gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            id="address-search"
            value={postcodeValue}
            onChange={(e) => handleManualInput(e.target.value)}
            placeholder={"Enter postcode (eg. SW1A 1AA)"}
            className={`pr-10 transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
            }`}
          />

          {/* Search/Loading/Error icon
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            {isSearching ? (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            ) : error ? (
              <AlertCircle className="w-4 h-4 text-red-500" />
            ) : (
              <Search className="w-4 h-4 text-gray-400" />
            )}
          </div> */}

        
        </div>

        {/* Error message */}
        {/* {error && (
          <div className="mt-1 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )} */}

        <Button
  onClick={fetchAddressesViaPostCode}
  disabled={isSearchingViaPostCode || postcodeValue.trim() == ""}
  className={`h-10 px-4 whitespace-nowrap bg-black hover:bg-gray-800 ${
    isDarkMode ? "text-white" : ""
  }`}
>
  Find address
</Button>

      </div>
        {errorPostCode && (
    <p className="mt-2 text-sm text-red-500">
    {String(errorPostCode)}
   </p>
  )}


      <div className="mt-3">
  <Label
    className={`transition-colors duration-300 ${
      isDarkMode ? "text-gray-200" : "text-gray-900"
    }`}
  >
    Select your address ({addresses.length} found)
  </Label>

    <select
    value={selectedIndex}
    onChange={(e) => handleSelectAddress(e.target.value)}
    className={`mt-1 w-full h-10 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 ${
      isDarkMode
        ? "bg-gray-800 border border-gray-600 text-white"
        : "bg-white border border-gray-300 text-gray-900"
    }`}
  >
    <option value="">
      {addresses.length === 0
        ? "Enter postcode and click Find address"
        : "Choose an address..."}
    </option>
    {addresses.map((addr, i) => (
  <option key={i} value={String(i)}>
    {[
      addr.addressLineOne,
      addr.addressLineTwo,
      addr.postalCounty,
      addr.postTown,
      addr.postCode,
    ]
      .filter(Boolean)
      .join(", ")}
  </option>
))}
  </select>
</div>

      
    </div>

    
  );
}

export default AddressSearchField;
